<!-- == Unichat Extension ==
     Modal para criar / editar uma Secretaria Unichat.
     Secretaria pertence a uma Prefeitura (Inbox).
-->
<script setup>
import { ref, watch, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import Button from 'dashboard/components-next/button/Button.vue';
import { createSector, updateSector } from 'dashboard/unichat/api/unichat.js';

const props = defineProps({
  sector: {
    type: Object,
    default: null,
  },
  accountId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['close', 'saved']);

const store = useStore();

// Lista de Prefeituras (Inboxes) do Chatwoot
const inboxes = useMapGetter('inboxes/getInboxes');

onMounted(() => {
  store.dispatch('inboxes/get');
});

const isEditing = ref(!!props.sector);
const isSaving = ref(false);

const form = ref({
  name: '',
  description: '',
  inbox_id: '',
  active: true,
});

// Preenche o formulário ao editar
watch(
  () => props.sector,
  val => {
    isEditing.value = !!val;
    form.value = val
      ? {
          name: val.name || '',
          description: val.description || '',
          inbox_id: val.inbox_id || '',
          active: val.active !== false,
        }
      : { name: '', description: '', inbox_id: '', active: true };
  },
  { immediate: true }
);

const onSubmit = async () => {
  if (!form.value.name.trim()) {
    useAlert('O nome da secretaria é obrigatório.');
    return;
  }
  if (!form.value.inbox_id) {
    useAlert('Selecione a Prefeitura (Caixa de Entrada) desta secretaria.');
    return;
  }
  isSaving.value = true;
  try {
    if (isEditing.value && props.sector?.id) {
      await updateSector(props.accountId, props.sector.id, form.value);
      useAlert('Secretaria atualizada com sucesso.');
    } else {
      await createSector(props.accountId, form.value);
      useAlert('Secretaria criada com sucesso.');
    }
    emit('saved');
  } catch (err) {
    const errors = err?.response?.data?.errors;
    const msg = (Array.isArray(errors) ? errors.join(', ') : err?.response?.data?.message) ||
      (isEditing.value ? 'Erro ao atualizar secretaria.' : 'Erro ao criar secretaria.');
    useAlert(msg);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <!-- Overlay -->
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="emit('close')"
  >
    <div
      class="bg-n-surface-1 rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 flex flex-col gap-5"
    >
      <!-- Título -->
      <div class="flex items-center justify-between">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ isEditing ? 'Editar Secretaria' : 'Nova Secretaria' }}
        </h3>
        <button
          class="text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="emit('close')"
        >
          ✕
        </button>
      </div>

      <!-- Formulário -->
      <form class="flex flex-col gap-4" @submit.prevent="onSubmit">

        <!-- Prefeitura (Inbox) -->
        <div class="flex flex-col gap-1">
          <label class="text-body-main font-medium text-n-slate-12">
            Prefeitura <span class="text-n-ruby-11">*</span>
          </label>
          <select
            v-model="form.inbox_id"
            class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-body-main text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
            required
          >
            <option value="" disabled>Selecione a Prefeitura...</option>
            <option
              v-for="inbox in inboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
          <p v-if="!inboxes?.length" class="text-xs text-n-slate-9">
            Nenhuma Prefeitura cadastrada. Crie uma em Configurações → Prefeituras.
          </p>
        </div>

        <!-- Nome -->
        <div class="flex flex-col gap-1">
          <label class="text-body-main font-medium text-n-slate-12">
            Nome <span class="text-n-ruby-11">*</span>
          </label>
          <input
            v-model="form.name"
            type="text"
            placeholder="Ex: Secretaria de Saúde, Sec. de Educação..."
            class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-body-main text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
            maxlength="100"
            required
          />
        </div>

        <!-- Descrição -->
        <div class="flex flex-col gap-1">
          <label class="text-body-main font-medium text-n-slate-12">
            Descrição
          </label>
          <textarea
            v-model="form.description"
            placeholder="Descreva a secretaria..."
            rows="2"
            class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-body-main text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
            maxlength="500"
          />
        </div>

        <!-- Ativo -->
        <label class="flex items-center gap-3 cursor-pointer">
          <input
            v-model="form.active"
            type="checkbox"
            class="w-4 h-4 rounded accent-n-brand"
          />
          <span class="text-body-main text-n-slate-12">Secretaria ativa</span>
        </label>

        <!-- Botões -->
        <div class="flex justify-end gap-3 pt-2">
          <Button
            label="Cancelar"
            variant="clear"
            @click="emit('close')"
          />
          <Button
            :label="isEditing ? 'Salvar alterações' : 'Criar secretaria'"
            :is-loading="isSaving"
            type="submit"
          />
        </div>
      </form>
    </div>
  </div>
</template>
